import {
  isQuickNoteOverdue,
  QUICK_NOTE_COLOR_LABELS,
  QUICK_NOTE_COLORS,
  QUICK_NOTE_MAX_CHARS,
  QUICK_NOTE_MESSAGES,
  type QuickNoteColor,
  type QuickNoteDto,
} from '@fos/shared/contracts/quick-notes';
import { addDaysLocal, formatDayShort, todayLocal } from '@fos/shared/time';
import { CalendarDays, CalendarX, Check, MoreHorizontal, Pencil, SquareCheckBig, StickyNote } from 'lucide-react';
import {
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { errorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button, IconButton } from '../../../ui/Button';
import { CountPill } from '../../../ui/CountPill';
import { Input } from '../../../ui/Input';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '../../../ui/Menu';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Skeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { TOUCH_ICON, useIsPhone } from '../../mobile';
import { DictationStatusLine, MicButton } from '../../voice/components/MicButton';
import { useDictation } from '../../voice/dictation';
import { useSpeechStatus } from '../../voice/speech-status';
import { useQuickNotes, useQuickNotesCache } from '../quick-notes-data';

/**
 * „Szybkie notatki” na Dziś — karteczki (DECISIONS 2026-10-17 [S]/[P] „Szybkie notatki na »Dziś« (karteczki)”):
 * wrzutki osoby, które nie są zadaniami („pojechać na pocztę”). Prywatne — widzi je tylko autor (usługi `notes.*`).
 *
 *  - pole „Zanotuj…”: Enter = zapisz; mikrofon = dyktowanie jak w Copilocie (`useDictation`, ten sam mechanizm
 *    z `mergeTranscripts` i poprawką Androida; bez zapisu nagrania); bez wsparcia przeglądarki — tylko pisanie,
 *  - karteczka: papierowe tło (żółta domyślna, róż, błękit, zieleń; tokeny `--note-*`, ciemny motyw przyciemniony),
 *    lekki cień i „taśma”, delikatny obrót ±1–2° znikający przy najechaniu i fokusie (bez ruchu przy
 *    `prefers-reduced-motion`), czytelny tekst,
 *  - akcje: odhacz (znika, toast „Cofnij”), edycja w miejscu (klik w treść), kolor, „na kiedy” (po terminie lekki
 *    akcent), „Zamień w zadanie” (z modułem Zadania),
 *  - siatka na desktopie, poziomy rząd z przyciąganiem na telefonie; pusty stan — jedna linijka zachęty.
 * Ten sam blok i to samo miejsce w widokach Dzień / Tydzień / Miesiąc (montaż w `TodayPage`).
 */

const PAPER: Record<QuickNoteColor, string> = {
  yellow: 'bg-(--note-yellow)',
  pink: 'bg-(--note-pink)',
  blue: 'bg-(--note-blue)',
  green: 'bg-(--note-green)',
};

/** Delikatny, stały obrót karteczki (z identyfikatora — ta sama karteczka zawsze tak samo). */
const TILTS = ['-1.5deg', '1deg', '-1deg', '1.75deg', '-2deg', '1.25deg'] as const;

function tiltOf(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return TILTS[Math.abs(h) % TILTS.length] as string;
}

function excerpt(text: string, max = 60): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1).trimEnd()}…` : flat;
}

type DueTone = 'overdue' | 'today' | 'later';

function dueInfo(note: QuickNoteDto, today: string): { text: string; tone: DueTone } | null {
  if (!note.dueDate) return null;
  if (isQuickNoteOverdue(note, today))
    return { text: `po terminie · ${formatDayShort(note.dueDate)}`, tone: 'overdue' };
  if (note.dueDate === today) return { text: 'na dziś', tone: 'today' };
  if (note.dueDate === addDaysLocal(today, 1)) return { text: 'na jutro', tone: 'later' };
  return { text: `na ${formatDayShort(note.dueDate)}`, tone: 'later' };
}

export function QuickNotesSection({ className }: { className?: string }) {
  const notes = useQuickNotes();
  const cache = useQuickNotesCache();
  const utils = trpc.useUtils();
  const access = useAccess();
  const phone = useIsPhone();
  const navigate = useNavigate();
  const headingId = useId();
  const today = todayLocal();
  const canTasks = access.can('tasks', 'edit');
  const items = notes.data ?? [];
  const listRef = useRef<HTMLUListElement>(null);
  // telefon: po dodaniu przewiń rząd do nowej karteczki (przyciąganie zostawiało rząd przy poprzedniej, nowa była
  // poza ekranem — przegląd 17.10.2026); czeka, aż karteczka pojawi się na liście
  const [added, setAdded] = useState<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `items` — ponów, gdy nowa karteczka trafi na listę
  useLayoutEffect(() => {
    if (!added) return;
    if (!phone) {
      setAdded(null);
      return;
    }
    const el = listRef.current?.querySelector<HTMLElement>(`[data-note-id="${added}"]`);
    if (!el) return;
    el.scrollIntoView?.({ inline: 'start', block: 'nearest' });
    setAdded(null);
  }, [added, phone, items]);

  const save = async (id: string, patch: { body?: string; color?: QuickNoteColor; dueDate?: string | null }) => {
    const before = cache.get().find((n) => n.id === id);
    if (before) cache.upsert({ ...before, ...patch });
    try {
      cache.upsert(await utils.client.quickNotes.update.mutate({ id, ...patch }));
    } catch (err) {
      if (before) cache.upsert(before);
      toast.error(errorMessage(err));
    }
  };

  const reopen = async (note: QuickNoteDto) => {
    try {
      cache.upsert(await utils.client.quickNotes.reopen.mutate({ id: note.id }));
      void cache.refresh();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const complete = async (note: QuickNoteDto) => {
    cache.remove(note.id);
    try {
      await utils.client.quickNotes.complete.mutate({ id: note.id });
      cache.settle();
      toast(QUICK_NOTE_MESSAGES.completed, {
        description: excerpt(note.body),
        action: { label: 'Cofnij', onClick: () => void reopen(note) },
      });
    } catch (err) {
      cache.upsert(note);
      toast.error(errorMessage(err));
    }
  };

  const toTask = async (note: QuickNoteDto) => {
    try {
      const res = await utils.client.quickNotes.toTask.mutate({ id: note.id });
      cache.remove(note.id);
      cache.settle();
      void utils.tasks.invalidate();
      toast(`${QUICK_NOTE_MESSAGES.converted} #${res.task.number}`, {
        description: excerpt(res.task.title),
        action: { label: 'Otwórz', onClick: () => navigate(`/zadania/${res.task.id}`) },
      });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <Section aria-labelledby={headingId} className={cn('min-w-0', className)}>
      <SectionHeader
        title={
          <span id={headingId} className="inline-flex items-center gap-2">
            Szybkie notatki
            {items.length > 0 && <CountPill count={items.length} label={`${items.length} otwartych`} />}
          </span>
        }
      />
      <Composer
        onAdded={(note) => {
          cache.upsert(note);
          setAdded(note.id);
        }}
      />

      {notes.isPending ? (
        <div className="mt-4 flex gap-3">
          <Skeleton className="h-[112px] w-[200px] rounded-md" />
          <Skeleton className="h-[112px] w-[200px] rounded-md" />
        </div>
      ) : notes.isError && !notes.data ? (
        <p className="mt-3 flex flex-wrap items-center gap-2 text-body-s text-fg-muted">
          Nie udało się wczytać notatek.
          <Button variant="ghost" size="s" onClick={() => void notes.refetch()} disabled={notes.isFetching}>
            Wczytaj notatki ponownie
          </Button>
        </p>
      ) : items.length === 0 ? (
        <p className="mt-3 text-body-s text-fg-muted">{QUICK_NOTE_MESSAGES.emptyHint}</p>
      ) : (
        <ul
          ref={listRef}
          aria-label="Karteczki"
          className={cn(
            'mt-4',
            phone
              ? // telefon: poziomy rząd z przyciąganiem (karteczki obok siebie, przewijanie palcem)
                '-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-px-4 px-4 pt-3.5 pb-4'
              : 'grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-4 pt-2',
          )}
        >
          {items.map((note) => (
            <li
              key={note.id}
              data-note-id={note.id}
              className={cn(phone && 'w-[72%] max-w-[260px] shrink-0 snap-start')}
            >
              <NoteCard
                note={note}
                today={today}
                canTasks={canTasks}
                onComplete={() => void complete(note)}
                onSave={(patch) => void save(note.id, patch)}
                onToTask={() => void toTask(note)}
              />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ── Pole „Zanotuj…” ─────────────────────────────────────────────────────────

function Composer({ onAdded }: { onAdded: (note: QuickNoteDto) => void }) {
  const utils = trpc.useUtils();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const speech = useSpeechStatus();
  const following = useRef(false);
  const dictation = useDictation({
    lang: 'pl-PL',
    engine: speech.engine,
    serverAvailable: speech.serverAvailable,
    serverMessage: speech.serverMessage,
    onFinished(spoken) {
      following.current = false;
      setText(spoken.slice(0, QUICK_NOTE_MAX_CHARS));
      inputRef.current?.focus();
    },
  });
  // tekst z dyktowania na żywo w polu (do poprawy przed zapisem)
  useEffect(() => {
    if (!following.current) return;
    setText([dictation.finalText, dictation.interimText].filter(Boolean).join(' ').slice(0, QUICK_NOTE_MAX_CHARS));
  }, [dictation.finalText, dictation.interimText]);
  // „Zanotuj” z menu telefonu (`/dzis#zanotuj`): pole od razu z fokusem
  const location = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: każde przejście (także ponowne na ten sam adres) = fokus
  useEffect(() => {
    if (location.hash !== '#zanotuj') return;
    const el = inputRef.current;
    el?.focus();
    el?.scrollIntoView?.({ block: 'center' });
  }, [location.hash, location.key]);

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    const body = text.trim();
    if (!body || busy) return;
    if (dictation.active) {
      following.current = false;
      dictation.cancel();
    }
    setBusy(true);
    try {
      const note = await utils.client.quickNotes.add.mutate({ body });
      onAdded(note);
      setText('');
      dictation.reset();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <form onSubmit={(e) => void submit(e)} className="flex items-center gap-2">
        <Input
          ref={inputRef}
          value={text}
          onChange={(e) => {
            following.current = false;
            setText(e.target.value);
          }}
          placeholder="Zanotuj…"
          aria-label="Nowa szybka notatka"
          maxLength={QUICK_NOTE_MAX_CHARS}
          icon={StickyNote}
          enterKeyHint="done"
          autoComplete="off"
          className="min-w-0"
        />
        {dictation.supported && (
          <MicButton
            dictation={dictation}
            base={text}
            disabled={busy}
            onStart={() => {
              following.current = true;
            }}
          />
        )}
        {text.trim() && (
          <Button type="submit" variant="secondary" disabled={busy} className="shrink-0">
            Zapisz
          </Button>
        )}
      </form>
      <DictationStatusLine dictation={dictation} className="mt-1.5" />
    </div>
  );
}

// ── Karteczka ───────────────────────────────────────────────────────────────

function NoteCard({
  note,
  today,
  canTasks,
  onComplete,
  onSave,
  onToTask,
}: {
  note: QuickNoteDto;
  today: string;
  canTasks: boolean;
  onComplete: () => void;
  onSave: (patch: { body?: string; color?: QuickNoteColor; dueDate?: string | null }) => void;
  onToTask: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [pickDate, setPickDate] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const dateRef = useRef<HTMLInputElement>(null);
  // akcja z menu, która przejmuje fokus po zamknięciu menu (inaczej Radix oddaje go przyciskowi ⋯)
  const afterMenu = useRef<'edit' | 'date' | null>(null);
  const due = dueInfo(note, today);
  const preview = excerpt(note.body, 40);

  useEffect(() => {
    if (pickDate) dateRef.current?.focus();
  }, [pickDate]);

  useEffect(() => {
    if (!editing) return;
    const el = textRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [editing]);

  const startEdit = () => {
    setDraft(note.body);
    setEditing(true);
  };
  const finishEdit = () => {
    setEditing(false);
    const body = draft.trim();
    if (body && body !== note.body) onSave({ body });
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      finishEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditing(false);
    }
  };

  return (
    <article
      aria-label={`Notatka: ${preview}`}
      style={{ '--tilt': tiltOf(note.id) } as CSSProperties}
      className={cn(
        'group relative flex min-h-[112px] flex-col rounded-[3px] px-3.5 pt-4 pb-2.5 text-(--note-fg) shadow-(--note-shadow)',
        PAPER[note.color],
        'rotate-(--tilt) hover:rotate-0 focus-within:rotate-0 motion-reduce:rotate-0',
        'motion-safe:transition-[rotate,translate] motion-safe:duration-200 hover:-translate-y-0.5',
      )}
    >
      {/* „taśma” — papierowy akcent */}
      <span
        aria-hidden
        className="pointer-events-none absolute -top-2 left-1/2 h-4 w-12 -translate-x-1/2 -rotate-2 rounded-[2px] bg-(--note-tape) shadow-xs"
      />
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={onComplete}
          aria-label={`Odhacz: ${preview}`}
          title="Odhacz"
          className={cn(
            // telefon: pole dotyku 44 px (kółko 20 px + 12 px z każdej strony), bez zmiany wyglądu
            "relative max-md:after:absolute max-md:after:-inset-3 max-md:after:content-['']",
            'mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full border-[1.5px] border-(--note-fg-muted)',
            'text-transparent transition-colors hover:bg-(--note-fg) hover:text-(--note-yellow) focus-visible:text-(--note-fg-muted)',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          )}
        >
          <Check size={12} strokeWidth={3} aria-hidden />
        </button>
        {editing ? (
          <textarea
            ref={textRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={finishEdit}
            onKeyDown={onKey}
            maxLength={QUICK_NOTE_MAX_CHARS}
            rows={Math.min(8, Math.max(2, draft.split('\n').length + 1))}
            aria-label="Treść notatki"
            className="min-w-0 flex-1 resize-none rounded-[2px] bg-(--note-tape) px-1 py-0.5 text-body leading-snug outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
          />
        ) : (
          <button
            type="button"
            onClick={startEdit}
            title="Kliknij, aby edytować"
            className="min-w-0 flex-1 cursor-text rounded-[2px] text-left text-body leading-snug break-words whitespace-pre-wrap focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            {note.body}
          </button>
        )}
      </div>

      <div className="mt-auto flex min-h-7 items-center gap-1 pt-2">
        {pickDate ? (
          <input
            type="date"
            lang="pl-PL"
            aria-label="Na kiedy"
            defaultValue={note.dueDate ?? today}
            ref={dateRef}
            onChange={(e) => {
              if (e.target.value) onSave({ dueDate: e.target.value });
            }}
            onBlur={() => setPickDate(false)}
            className="h-7 rounded-md border border-(--note-fg-muted) bg-(--note-tape) px-1.5 text-caption text-(--note-fg) tabular"
          />
        ) : due ? (
          <span
            className={cn(
              'inline-flex items-center gap-1 text-caption',
              due.tone === 'overdue'
                ? 'font-medium text-(--note-overdue)'
                : due.tone === 'today'
                  ? 'font-medium text-(--note-today)'
                  : 'text-(--note-fg-muted)',
            )}
          >
            <CalendarDays size={13} strokeWidth={1.75} aria-hidden />
            {due.text}
          </span>
        ) : null}
        <Menu>
          <MenuTrigger asChild>
            <IconButton
              icon={MoreHorizontal}
              label={`Więcej: ${preview}`}
              className={cn(
                'relative ml-auto text-(--note-fg-muted) hover:bg-black/5 hover:text-(--note-fg)',
                TOUCH_ICON,
              )}
            />
          </MenuTrigger>
          <MenuContent
            align="end"
            onCloseAutoFocus={(e) => {
              const next = afterMenu.current;
              afterMenu.current = null;
              if (!next) return;
              e.preventDefault();
              if (next === 'edit') startEdit();
              else setPickDate(true);
            }}
          >
            <MenuItem
              icon={Pencil}
              onSelect={() => {
                afterMenu.current = 'edit';
              }}
            >
              Edytuj
            </MenuItem>
            <MenuSeparator />
            <MenuLabel>Na kiedy</MenuLabel>
            <MenuItem icon={CalendarDays} onSelect={() => onSave({ dueDate: today })}>
              Na dziś
            </MenuItem>
            <MenuItem icon={CalendarDays} onSelect={() => onSave({ dueDate: addDaysLocal(today, 1) })}>
              Na jutro
            </MenuItem>
            <MenuItem
              icon={CalendarDays}
              onSelect={() => {
                afterMenu.current = 'date';
              }}
            >
              Wybierz datę…
            </MenuItem>
            {note.dueDate && (
              <MenuItem icon={CalendarX} onSelect={() => onSave({ dueDate: null })}>
                Bez terminu
              </MenuItem>
            )}
            <MenuSeparator />
            <MenuLabel>Kolor</MenuLabel>
            <MenuRadioGroup value={note.color} onValueChange={(v) => onSave({ color: v as QuickNoteColor })}>
              {QUICK_NOTE_COLORS.map((c) => (
                <MenuRadioItem key={c} value={c}>
                  <span
                    aria-hidden
                    className={cn(
                      'mr-2 inline-block size-3.5 rounded-[3px] border border-black/10 align-[-2px]',
                      PAPER[c],
                    )}
                  />
                  {QUICK_NOTE_COLOR_LABELS[c]}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
            {canTasks && (
              <>
                <MenuSeparator />
                <MenuItem icon={SquareCheckBig} onSelect={onToTask}>
                  Zamień w zadanie
                </MenuItem>
              </>
            )}
          </MenuContent>
        </Menu>
      </div>
    </article>
  );
}
